import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem
} from '@mui/material';
import {
  LocationOn,
  LocationOff,
  AccessTime,
  ExitToApp,
  HomeWork,
  CheckCircle,
  Cancel,
  Pending,
  History
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';

interface LocationData {
  latitude: number;
  longitude: number;
  accuracy: number;
  address?: string;
}

interface AttendanceRecord {
  _id: string;
  date: string;
  user?: {
    _id: string;
    name: string;
    email: string;
    employeeId: string;
    designation?: string;
    department?: string;
  };
  checkIn: {
    time: string;
    location: LocationData;
    isWithinOffice: boolean;
    distanceFromOffice: number;
  };
  checkOut?: {
    time: string;
    location: LocationData;
    isWithinOffice: boolean;
    distanceFromOffice: number;
  };
  totalHours: number;
  overtime: number;
  status: string;
}

interface WFHRequest {
  _id: string;
  user: {
    _id: string;
    name: string;
    email: string;
    employeeId: string;
  };
  date: string;
  reason: string;
  status: 'pending' | 'approved' | 'rejected';
  approvedBy?: {
    _id: string;
    name: string;
  };
  approvedAt?: string;
  rejectedBy?: {
    _id: string;
    name: string;
  };
  rejectedAt?: string;
  rejectionReason?: string;
  createdAt: string;
}

/** Block next check-in until this many ms have passed since last check-in time. */
const CHECK_IN_COOLDOWN_MS = 20 * 60 * 60 * 1000;

const Attendance: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [wfhSuccess, setWfhSuccess] = useState<string | null>(null);
  const [currentLocation, setCurrentLocation] = useState<LocationData | null>(null);
  const [isWithinOffice, setIsWithinOffice] = useState<boolean>(false);
  const [todayAttendance, setTodayAttendance] = useState<any>(null);
  const [attendanceHistory, setAttendanceHistory] = useState<AttendanceRecord[]>([]);
  const [officeLocation, setOfficeLocation] = useState<any>(null);
  
  // WFH related states
  const [wfhDialogOpen, setWfhDialogOpen] = useState(false);
  const [wfhDate, setWfhDate] = useState('');
  const [wfhReason, setWfhReason] = useState('');
  const [wfhRequests, setWfhRequests] = useState<WFHRequest[]>([]);
  const [hasApprovedWFHToday, setHasApprovedWFHToday] = useState(false);

  // Get current location
  const getCurrentLocation = (): Promise<LocationData> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const acc = position.coords.accuracy;
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy:
              typeof acc === 'number' && Number.isFinite(acc) ? acc : 0,
            address: 'Getting address...'
          });
        },
        (error) => {
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    });
  };

  // Get address from coordinates (reverse geocoding)
  const getAddressFromCoordinates = async (lat: number, lng: number): Promise<string> => {
    try {
      const response = await fetch(
        `/api/geocoding/reverse-geocode?lat=${lat}&lon=${lng}`
      );
      const data = await response.json();
      return data.display_name || 'Unknown location';
    } catch (error) {
      console.error('Error getting address:', error);
      return 'Unknown location';
    }
  };

  // Calculate distance between two coordinates
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371000; // Earth's radius in meters
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in meters
  };

  // Load today's attendance (only for employees)
  const loadTodayAttendance = async () => {
    try {
      if (user?.role === 'admin') {
        // Admins don't need to see their own today's attendance
        setTodayAttendance(null);
        return;
      }
      const response = await apiService.getTodayAttendance();
      // API returns { attendance?, canCheckIn, canCheckOut, ... } — flatten attendance doc for UI
      if (response.attendance) {
        setTodayAttendance({
          ...response.attendance,
          canCheckIn: response.canCheckIn,
          canCheckOut: response.canCheckOut,
        });
      } else {
        setTodayAttendance({
          canCheckIn: response.canCheckIn !== false,
          canCheckOut: !!response.canCheckOut,
        });
      }
    } catch (error: any) {
      console.error('Error loading today attendance:', error);
      setError(error?.response?.data?.message || 'Failed to load attendance');
    }
  };

  // Load attendance history
  const loadAttendanceHistory = async () => {
    try {
      let response;
      if (user?.role === 'admin') {
        // Admin sees all employees' attendance
        response = await apiService.getAllAttendance();
        setAttendanceHistory(response.attendance || []);
      } else {
        // Employee sees only their own attendance
        response = await apiService.getAttendance();
        setAttendanceHistory(response.attendance || []);
      }
    } catch (error: any) {
      console.error('Error loading attendance history:', error);
      setError(error?.response?.data?.message || 'Failed to load attendance history');
    }
  };

  // Check-in with location validation
  const handleCheckIn = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      let location = currentLocation;
      if (!location) {
        setError('Getting your location...');
        try {
          location = await getCurrentLocation();
        } catch (geoErr: any) {
          const code = geoErr?.code ?? geoErr?.PERMISSION_DENIED;
          if (code === 1) {
            setError(
              'Location permission denied. Allow location access in your browser to check in.'
            );
          } else {
            setError(geoErr?.message || 'Unable to get your location.');
          }
          setLoading(false);
          return;
        }

        const address = await getAddressFromCoordinates(
          location.latitude,
          location.longitude
        );

        setCurrentLocation({
          ...location,
          address
        });
      }

      const latitude = Number(location.latitude);
      const longitude = Number(location.longitude);
      const accuracy =
        typeof location.accuracy === 'number' && Number.isFinite(location.accuracy)
          ? location.accuracy
          : 0;

      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        setError('Invalid location. Please refresh and try again.');
        setLoading(false);
        return;
      }

      const addressStr =
        location.address ||
        (await getAddressFromCoordinates(latitude, longitude));

      if (!hasApprovedWFHToday) {
        const officeLat = Number(officeLocation?.coordinates?.latitude);
        const officeLng = Number(officeLocation?.coordinates?.longitude);
        const radius = Number(officeLocation?.coordinates?.radius) || 100;

        if (!Number.isFinite(officeLat) || !Number.isFinite(officeLng)) {
          setError(
            'Office location is not available. Please try again in a moment.'
          );
          setLoading(false);
          return;
        }

        const distance = calculateDistance(
          latitude,
          longitude,
          officeLat,
          officeLng
        );
        const withinOffice = distance <= radius;
        setIsWithinOffice(withinOffice);

        if (!withinOffice) {
          setError('You are not at office location. Check-in not allowed.');
          setLoading(false);
          return;
        }
      } else {
        setIsWithinOffice(false);
      }

      console.log('CHECK-IN DATA:', {
        latitude,
        longitude,
        accuracy
      });

      const response = await apiService.checkIn({
        latitude,
        longitude,
        accuracy,
        address: addressStr || 'Unknown location'
      });

      setSuccess(response.message);
      
      // Show location validation details if available
      if (response.location && !response.location.isWithinOffice) {
        setError(response.location.message);
      }
      
      if (response.attendance) {
        setTodayAttendance({
          ...response.attendance,
          canCheckIn: false,
          canCheckOut: true
        });
      }
      
      await loadTodayAttendance();
      await loadAttendanceHistory();
    } catch (error: any) {
      console.error('Check-in error:', error?.response?.data || error);
      const data = error?.response?.data;
      const fromArray =
        Array.isArray(data?.errors) && data.errors[0]?.msg
          ? data.errors[0].msg
          : null;
      setError(data?.message || fromArray || 'Check-in failed');
    } finally {
      setLoading(false);
    }
  };

  // Check-out with location validation
  const handleCheckOut = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      let location = currentLocation;
      if (!location) {
        setError('Getting your location...');
        try {
          location = await getCurrentLocation();
        } catch (geoErr: any) {
          const code = geoErr?.code ?? geoErr?.PERMISSION_DENIED;
          if (code === 1) {
            setError(
              'Location permission denied. Allow location access in your browser to check out.'
            );
          } else {
            setError(geoErr?.message || 'Unable to get your location.');
          }
          setLoading(false);
          return;
        }

        const address = await getAddressFromCoordinates(
          location.latitude,
          location.longitude
        );

        setCurrentLocation({
          ...location,
          address
        });
      }

      const latitude = Number(location.latitude);
      const longitude = Number(location.longitude);
      const accuracy =
        typeof location.accuracy === 'number' && Number.isFinite(location.accuracy)
          ? location.accuracy
          : 0;

      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        setError('Invalid location. Please refresh and try again.');
        setLoading(false);
        return;
      }

      const addressStr =
        location.address ||
        (await getAddressFromCoordinates(latitude, longitude));

      console.log('CHECK-OUT DATA:', { latitude, longitude, accuracy });

      const response = await apiService.checkOut({
        latitude,
        longitude,
        accuracy,
        address: addressStr || 'Unknown location'
      });

      setSuccess(response.message);
      
      // Show location validation details if available
      if (response.location && !response.location.isWithinOffice) {
        setError(response.location.message);
      }
      
      if (response.attendance) {
        setTodayAttendance({
          ...response.attendance,
          canCheckIn: false,
          canCheckOut: false
        });
      }
      
      await loadTodayAttendance();
      await loadAttendanceHistory();
    } catch (error: any) {
      console.error('Check-out error:', error?.response?.data || error);
      const data = error?.response?.data;
      const fromArray =
        Array.isArray(data?.errors) && data.errors[0]?.msg
          ? data.errors[0].msg
          : null;
      setError(data?.message || fromArray || 'Check-out failed');
    } finally {
      setLoading(false);
    }
  };

  // Load office location configuration
  const loadOfficeLocation = async () => {
    try {
      const response = await apiService.getOfficeLocation();
      setOfficeLocation(response.officeLocation);
    } catch (error: any) {
      console.error('Error loading office location:', error);
      setError(error?.response?.data?.message || 'Failed to load office location');
    }
  };

  // WFH request functions
  const loadWFHRequests = async () => {
    try {
      const response = await apiService.getUserWFHRequests(1, 10);
      const previousRequests = wfhRequests;
      setWfhRequests(response.data);
      
      // Check for status changes and show appropriate messages
      if (previousRequests.length > 0) {
        const updatedRequests = response.data;
        for (const updatedRequest of updatedRequests) {
          const previousRequest = previousRequests.find(req => req._id === updatedRequest._id);
          if (previousRequest && previousRequest.status === 'pending' && updatedRequest.status !== 'pending') {
            if (updatedRequest.status === 'approved') {
              setWfhSuccess(`Your WFH request for ${formatDate(updatedRequest.date)} has been approved! 🎉`);
            } else if (updatedRequest.status === 'rejected') {
              setWfhSuccess(`Your WFH request for ${formatDate(updatedRequest.date)} has been rejected.`);
            }
            // Clear the message after 5 seconds
            setTimeout(() => setWfhSuccess(null), 5000);
            break; // Only show the most recent status change
          }
        }
      }
    } catch (error: any) {
      console.error('Error loading WFH requests:', error);
    }
  };

  const checkTodayWFHStatus = async () => {
    try {
      const response = await apiService.checkTodayWFHStatus();
      setHasApprovedWFHToday(response.hasApprovedWFH);
    } catch (error: any) {
      console.error('Error checking today WFH status:', error);
    }
  };

  const handleWFHRequest = async () => {
    if (!wfhDate || !wfhReason) {
      setError('Please fill in all fields');
      return;
    }

    if (wfhReason.length < 10) {
      setError('Reason must be at least 10 characters long');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await apiService.createWFHRequest({
        date: wfhDate,
        reason: wfhReason
      });

      setSuccess('WFH request submitted successfully');
      setWfhDialogOpen(false);
      setWfhDate('');
      setWfhReason('');
      loadWFHRequests();
    } catch (error: any) {
      setError(error?.response?.data?.message || 'Failed to submit WFH request');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTodayAttendance();
    loadAttendanceHistory();
    loadOfficeLocation();
    loadWFHRequests();
    checkTodayWFHStatus();
  }, []);

  /** Most recent check-in time from today's row or latest history row (for 20h cooldown). */
  const lastCheckInTimeMs = useMemo(() => {
    const fromToday = todayAttendance?.checkIn?.time;
    if (fromToday) return new Date(fromToday).getTime();
    const fromHistory = attendanceHistory[0]?.checkIn?.time;
    if (fromHistory) return new Date(fromHistory).getTime();
    return null;
  }, [todayAttendance, attendanceHistory]);

  const checkInCooldownEndsAt = useMemo(() => {
    if (lastCheckInTimeMs == null) return null;
    return new Date(lastCheckInTimeMs + CHECK_IN_COOLDOWN_MS);
  }, [lastCheckInTimeMs]);

  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    if (lastCheckInTimeMs == null) return undefined;
    const id = window.setInterval(() => setNowTick(Date.now()), 60_000);
    return () => clearInterval(id);
  }, [lastCheckInTimeMs]);

  const checkInCooldownActive = useMemo(() => {
    if (lastCheckInTimeMs == null) return false;
    return nowTick - lastCheckInTimeMs < CHECK_IN_COOLDOWN_MS;
  }, [lastCheckInTimeMs, nowTick]);

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {user?.role === 'admin' ? 'Employee Attendance Management' : 'Attendance Management'}
      </Typography>

      {/* Today's Attendance - Only for Employees */}
      {user?.role !== 'admin' && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Today's Attendance
            </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {wfhSuccess && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {wfhSuccess}
            </Alert>
          )}

          {/* Current Location Display */}
          {currentLocation && (
            <Box sx={{ mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                📍 Current Location Details
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Coordinates:</strong> {currentLocation?.latitude?.toFixed(6) || 'N/A'}, {currentLocation?.longitude?.toFixed(6) || 'N/A'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Address:</strong> {currentLocation?.address || 'Fetching address...'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Accuracy:</strong> ±{currentLocation?.accuracy || 'N/A'}m
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Distance from Office:</strong> {isWithinOffice ? 'Within office' : 'Outside office'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Status:</strong> 
                <Chip 
                  label={isWithinOffice ? 'Within Office' : 'Outside Office'} 
                  color={isWithinOffice ? 'success' : 'warning'}
                  size="small"
                  sx={{ ml: 1 }}
                />
              </Typography>
            </Box>
          )}

          {/* Office Location Display */}
          {officeLocation && (
            <Box sx={{ mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#f5f5f5' }}>
              <Typography variant="subtitle2" gutterBottom>
                🏢 Office Location Details
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Name:</strong> {officeLocation?.name || 'N/A'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Address:</strong> {officeLocation?.address || 'N/A'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Coordinates:</strong> {officeLocation.coordinates?.latitude?.toFixed(6) || 'N/A'}, {officeLocation.coordinates?.longitude?.toFixed(6) || 'N/A'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Radius:</strong> {officeLocation.coordinates?.radius || 'N/A'}m
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Timezone:</strong> {officeLocation?.timezone || 'N/A'}
              </Typography>
            </Box>
          )}

          {/* WFH Request Section */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Work From Home Request
            </Typography>
            {hasApprovedWFHToday ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                You have an approved WFH request for today. You can check in from home.
              </Alert>
            ) : (
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<HomeWork />}
                  onClick={() => setWfhDialogOpen(true)}
                  sx={{ flex: 1 }}
                >
                  Request Work From Home
                </Button>
              </Box>
            )}
          </Box>

          {todayAttendance ? (
            <Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: '50%', md: '50%' } }}>
                  {todayAttendance?.checkIn ? (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" color="textSecondary">
                        Time: {formatTime(todayAttendance.checkIn.time)}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Location: {todayAttendance.checkIn.location.address}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Distance: {todayAttendance.checkIn.distanceFromOffice}m from office
                      </Typography>
                      <Chip
                        label={todayAttendance.checkIn.isWithinOffice ? 'Within Office' : 'Outside Office'}
                        color={todayAttendance.checkIn.isWithinOffice ? 'success' : 'warning'}
                        size="small"
                      />
                    </Box>
                  ) : checkInCooldownActive ? (
                    <Alert severity="info" sx={{ width: '100%' }}>
                      Check-in is available again after{' '}
                      <strong>{checkInCooldownEndsAt?.toLocaleString() ?? '—'}</strong> (20 hours from your last
                      check-in).
                    </Alert>
                  ) : (
                    <Button
                      variant="contained"
                      startIcon={<AccessTime />}
                      onClick={handleCheckIn}
                      disabled={loading}
                      sx={{ width: '100%' }}
                    >
                      {loading ? 'Checking In...' : 'Check In'}
                    </Button>
                  )}
                </Box>

                <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: '50%', md: '50%' } }}>
                  {todayAttendance?.checkIn && !todayAttendance?.checkOut ? (
                    <Button
                      variant="contained"
                      color="secondary"
                      startIcon={<ExitToApp />}
                      onClick={handleCheckOut}
                      disabled={loading}
                      sx={{ width: '100%' }}
                    >
                      {loading ? 'Checking Out...' : 'Check Out'}
                    </Button>
                  ) : todayAttendance?.checkOut ? (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" color="textSecondary">
                        Time: {formatTime(todayAttendance.checkOut.time)}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Location: {todayAttendance.checkOut.location.address}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Distance: {todayAttendance.checkOut.distanceFromOffice}m from office
                      </Typography>
                      <Chip
                        label={todayAttendance.checkOut.isWithinOffice ? 'Within Office' : 'Outside Office'}
                        color={todayAttendance.checkOut.isWithinOffice ? 'success' : 'warning'}
                        size="small"
                      />
                    </Box>
                  ) : null}
                </Box>

                {(todayAttendance.totalHours > 0 || todayAttendance.overtime > 0) && (
                  <Box sx={{ flex: 1, minWidth: '100%' }}>
                    <Typography variant="subtitle1">
                      Work Summary
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Total Hours: {todayAttendance.totalHours}h
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Overtime: {todayAttendance.overtime}h
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">
              No attendance record for today
            </Typography>
          )}
        </CardContent>
      </Card>
      )}

      {/* WFH Requests History - Only for Employees */}
      {user?.role !== 'admin' && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <History />
              WFH Request History
            </Typography>
            
            {wfhRequests.length > 0 ? (
              <List>
                {wfhRequests.map((request, index) => (
                  <div key={request._id}>
                    <ListItem>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="subtitle1">
                              {formatDate(request.date)}
                            </Typography>
                            <Chip
                              icon={
                                request.status === 'approved' ? <CheckCircle /> :
                                request.status === 'rejected' ? <Cancel /> :
                                <Pending />
                              }
                              label={request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                              color={
                                request.status === 'approved' ? 'success' :
                                request.status === 'rejected' ? 'error' :
                                'warning'
                              }
                              size="small"
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                              <strong>Reason:</strong> {request.reason}
                            </Typography>
                            {request.status === 'approved' && request.approvedBy && (
                              <Typography variant="body2" color="success.main">
                                <CheckCircle sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                                Approved by {request.approvedBy.name} on {formatDate(request.approvedAt || request.createdAt)}
                              </Typography>
                            )}
                            {request.status === 'rejected' && request.rejectedBy && (
                              <Box>
                                <Typography variant="body2" color="error.main" sx={{ mb: 0.5 }}>
                                  <Cancel sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                                  Rejected by {request.rejectedBy.name} on {formatDate(request.rejectedAt || request.createdAt)}
                                </Typography>
                                {request.rejectionReason && (
                                  <Typography variant="body2" color="textSecondary">
                                    <strong>Reason:</strong> {request.rejectionReason}
                                  </Typography>
                                )}
                              </Box>
                            )}
                            {request.status === 'pending' && (
                              <Typography variant="body2" color="warning.main">
                                <Pending sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                                Request submitted on {formatDate(request.createdAt)} - Awaiting admin approval
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < wfhRequests.length - 1 && <Divider />}
                  </div>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="textSecondary">
                No WFH requests found. Click "Request Work From Home" to submit a new request.
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Admin Notice */}
      {user?.role === 'admin' && (
        <Card sx={{ mb: 3, backgroundColor: '#f5f5f5' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              Admin View - Employee Attendance
            </Typography>
            <Typography variant="body2" color="textSecondary">
              As an administrator, you can view all employees' attendance records below. 
              Individual check-in/check-out functionality is not available for admin accounts.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Attendance History */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {user?.role === 'admin' ? 'All Employees Attendance History' : 'Attendance History'}
          </Typography>
          
          {attendanceHistory.length > 0 ? (
            <List>
              {attendanceHistory.map((record, index) => (
                <div key={record._id}>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box>
                          <Typography variant="subtitle1" component="span">
                            {formatDate(record.date)}
                          </Typography>
                          {user?.role === 'admin' && record.user && (
                            <Typography variant="body2" component="span" sx={{ ml: 2 }}>
                              👤 {record.user.name} ({record.user.employeeId})
                              {record.user.designation && ` - ${record.user.designation}`}
                            </Typography>
                          )}
                          <Chip
                            label={record.status.toUpperCase()}
                            color={
                              record.status === 'present' ? 'success' :
                              record.status === 'late' ? 'warning' :
                              record.status === 'absent' ? 'error' :
                              record.status === 'wfh' ? 'info' : 'default'
                            }
                            size="small"
                            sx={{ ml: 1 }}
                            icon={record.status === 'wfh' ? <HomeWork /> : undefined}
                          />
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" color="textSecondary" component="div">
                          <Box>
                            {record.status === 'wfh' && (
                              <Typography variant="body2" color="primary.main" component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                                <HomeWork fontSize="small" />
                                Work From Home
                              </Typography>
                            )}
                            <Typography variant="body2" color="textSecondary" component="span">
                              Check-in: {record.checkIn ? formatTime(record.checkIn.time) : 'N/A'}
                            </Typography>
                            <br />
                            <Typography variant="body2" color="textSecondary" component="span">
                              Check-out: {record.checkOut ? formatTime(record.checkOut.time) : 'N/A'}
                            </Typography>
                            <br />
                            <Typography variant="body2" color="textSecondary" component="span">
                              Total Hours: {record.totalHours}h
                            </Typography>
                            {record.overtime > 0 && (
                              <>
                                <br />
                                <Typography variant="body2" color="textSecondary" component="span">
                                  Overtime: {record.overtime}h
                                </Typography>
                              </>
                            )}
                          </Box>
                        </Typography>
                      }
                    />
                  </ListItem>
                  {index < attendanceHistory.length - 1 && <Divider />}
                </div>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="textSecondary">
              No attendance history available
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* WFH Request Dialog */}
      <Dialog 
        open={wfhDialogOpen} 
        onClose={() => setWfhDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Request Work From Home
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Date"
              type="date"
              value={wfhDate}
              onChange={(e) => setWfhDate(e.target.value)}
              sx={{ mb: 2 }}
              inputProps={{
                min: new Date().toISOString().split('T')[0]
              }}
            />
            <TextField
              fullWidth
              label="Reason"
              multiline
              rows={4}
              value={wfhReason}
              onChange={(e) => setWfhReason(e.target.value)}
              placeholder="Please provide a reason for your work from home request..."
              helperText={`${wfhReason.length}/10 characters minimum`}
              sx={{ mb: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWfhDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleWFHRequest}
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Submitting...' : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Attendance;
